import { useController } from "react-hook-form";
import { RESIZE_VALUE, Textarea, useThemedStyletron } from "@bluecrew/blueprint-web";
import React, { useState } from "react";
import { styled } from "baseui";
import { ExamplesTitleContainer, ExamplesToggleButton } from "./ExamplesToggleButton";
import { FieldLabelRow } from "./FieldLabelRow";
import { FieldRowWrapper } from "./FieldRowWrapper";
import { errorMsg } from "./validationErrorMessages";
import { FieldTextLength } from "./FieldTextLength";
import { ArrivalInstructionsFieldProps } from "./types/propTypes/ArrivalInstructionsField.types";
import { Spacer, SpacerType } from "../../components/Spacer";
import { RelativeFlexFieldWrapper } from "./RelativeFlexFieldWrapper";
import { FieldContentExample } from "./FieldContentExample/FieldContentExample";

const ARRIVAL_MIN_LENGTH = 10;
const ARRIVAL_MAX_LENGTH = 500;

const arrivalInstructionsExamples = [
  {
    id: "1.1",
    text: (
      <span>
        ID / Certification (e.g., <i>Show ID upon arrival)</i>
      </span>
    ),
  },
  {
    id: "1.2",
    text: "Parking (e.g., lot location, street parking)",
  },
  {
    id: "1.3",
    text: "Public Transport (e.g., closest bus/train stop)",
  },
  {
    id: "1.4",
    text: (
      <span>
        Building (e.g., <i>Employee entrance is at Gate 3)</i>
      </span>
    ),
  },
  {
    id: "1.5",
    text: "Suite number (include, may not be on Google Maps address)",
  },
];

export const ArrivalInstructionsField = ({
  control,
  trigger,
  disabled = false,
  examplesEnabled = false,
}: ArrivalInstructionsFieldProps) => {
  const fieldName = "arrivalInstructions";
  const validate = trigger.bind(null, fieldName);
  const [examplesVisible, setExamplesVisible] = useState(false);

  const {
    field: { onChange, value },
    meta: { invalid },
  } = useController({
    name: fieldName,
    control,
    rules: {
      required: true,
      minLength: {
        value: ARRIVAL_MIN_LENGTH,
        message: errorMsg("min", ARRIVAL_MIN_LENGTH),
      },
      maxLength: {
        value: ARRIVAL_MAX_LENGTH,
        message: errorMsg("max", ARRIVAL_MAX_LENGTH),
      },
    },
  });

  const handleChange = (instructions: string) => {
    onChange(instructions);
  };

  const [, theme] = useThemedStyletron();
  const [fieldFocus, onFieldFocus] = useState(false);

  return (
    <RelativeFlexFieldWrapper data-pendo-key="ArrivalInstructionsEditField">
      <FieldRowWrapper>
        <ExamplesTitleContainer>
          <FieldLabelRow>Arrival instructions</FieldLabelRow>
          {examplesEnabled && (
            <ExamplesToggleButton onClick={() => setExamplesVisible(!examplesVisible)}>
              Examples
            </ExamplesToggleButton>
          )}
        </ExamplesTitleContainer>
        <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
        <InputWrapper>
          <Textarea
            value={value}
            error={invalid}
            onChange={(text) => handleChange(text)}
            onFocus={() => onFieldFocus(true)}
            onBlur={() => {
              onFieldFocus(false);
              validate();
            }}
            disabled={disabled}
            resize={RESIZE_VALUE.vertical}
            styles={{ minHeight: "188px" }}
          />
          <FieldTextLength
            focused={fieldFocus}
            error={invalid}
            minLength={ARRIVAL_MIN_LENGTH}
            maxLength={ARRIVAL_MAX_LENGTH}
            length={String(value).length}
          />
        </InputWrapper>

        <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
      </FieldRowWrapper>
      <FieldContentExample
        examples={arrivalInstructionsExamples}
        examplesVisible={examplesVisible}
      />
    </RelativeFlexFieldWrapper>
  );
};

const InputWrapper = styled("div", () => ({ width: "502.82px" }));
